import { ApplicationInsights, IEventTelemetry } from '@microsoft/applicationinsights-web';
import { correlationId, userIdStorage } from '../domain/services';
import { useAppState } from '../hooks/zustandHooks';

interface IObfuscatedLoggedEvent {
  event: string;
  obfuscated: string[];
}

interface TrackableEvent {
  type: string;
  payload?: any;
}

export const EventTypes = {
  INIT_STATE_SUCCESS: 'INIT STATE SUCCESS',
  INIT_STATE_ERROR: 'INIT STATE ERROR',
  COMPLETE_PROVIDER_SELECTION: 'COMPLETE PROVIDER SELECTION',
  PROVIDER_PRESELECTION: 'PROVIDER PRESELECTION',
  BANK_CODE_PRESELECTION: 'BANK CODE PRESELECTION',
  COMPLETE_CONNECTION: 'COMPLETE CONNECTION',
  CONSENT_ACCEPTED: 'CONSENT ACCEPTED',
  CONSENT_ACCEPTED_ERROR: 'CONSENT ACCEPTED ERROR',
  GET_TERMS_SUCCESS: 'GET TERMS SUCCESS',
  CREATE_CONSENT_SUCCESS: 'CREATE CONSENT SUCCESS',
  CREATE_CONSENT_ID_SUCCESS: 'CREATE CONSENT ID SUCCESS',
  CREATE_CONSENT_ID_ERROR: 'CREATE CONSENT ID ERROR',
  CREATE_CONSENT_ID_CONNECTION_ERROR: 'CREATE CONSENT ID CONNECTION ERROR',
  CREATE_CONSENT_ID_CONNECTION_SUCCESS: 'CREATE CONSENT ID CONNECTION SUCCESS',
  RECAPTCHA_UPDATE: 'RECAPTCHA UPDATE',
  RECAPTCHA_ERROR: 'RECAPTCHA ERROR',
  EXIT_APPLICATION: 'EXIT APPLICATION',
  REDIRECTION_ERROR: 'REDIRECTION ERROR',
  CREDIT_DATA_ACCOUNTS_ERROR: 'CREDIT DATA ACCOUNTS ERROR',
  TOKEN_ERROR: 'TOKEN ERROR',
  CIS_LOCAL_STORAGE_TOKEN: 'CIS LOCAL STORAGE TOKEN',
  CIS_PREVIOUS_UK_ADDRESS: 'CIS PREVIOUS UK ADDRESS',
  CIS_SUBMIT_PERSONAL_DETAILS: 'CIS SUBMIT PERSONAL DETAILS',
  CIS_SUBMIT_STORED_INVITE: 'CIS SUBMIT STORED INVITE',
  CIS_STORED_INVITE_VALIDATION_FAILED: 'CIS STORED INVITE VALIDATION FAILED',
  CIS_VALIDATION_FAILED: 'CIS VALIDATION FAILED',
  CIS_VERIFICATION_FAILED: 'CIS VERIFICATION FAILED',
  CIS_ACCOUNTS_FETCH_FAILED: 'CIS ACCOUNTS FETCH FAILED',
  CIS_OTP_SEND: 'CIS OTP SEND',
  CIS_OTP_VALIDATE: 'CIS OTP VALIDATE',
  CIS_OTP_RETRY: 'CIS OTP RETRY',
  CIS_OTP_EXPIRED: 'CIS OTP EXPIRED',
  CIS_OTP_VALIDATION_FAILED: 'CIS OTP VERIFICATION FAILED',
  CIS_OTP_VALIDATION_COMPLETE: 'CIS OTP VERIFICATION COMPLETE',
  CIS_KBA_FETCH: 'CIS KBA FETCH',
  CIS_KBA_VALIDATE: 'CIS KBA VALIDATE',
  CIS_KBA_RETRY: 'CIS KBA RETRY',
  CIS_KBA_REQUIRE_ADDITIONAL_QUESTIONS: 'CIS KBA REQUIRE ADDITIONAL QUESTIONS',
  CIS_KBA_VALIDATION_FAILED: 'CIS KBA VERIFICATION FAILED',
  CIS_KBA_VALIDATION_COMPLETE: 'CIS KBA VERIFICATION COMPLETE'
} as const;

const loggedEvents: (string | IObfuscatedLoggedEvent)[] = [
  ...Object.values(EventTypes),
  {
    event: 'VALIDATION_TOKEN_UPDATE',
    obfuscated: ['validationToken']
  }
];

export const ObfuscatedDataValue = 'obfuscatedData';

function canTrack(insights: ApplicationInsights) {
  return insights.config.instrumentationKey !== '';
}

function isIObfuscatedLoggedEvent(event: IObfuscatedLoggedEvent | string | undefined): event is IObfuscatedLoggedEvent {
  return event !== undefined && (event as IObfuscatedLoggedEvent).event !== undefined;
}

function shouldTrack(eventType: string, events: (string | IObfuscatedLoggedEvent)[] | '*'): boolean {
  if (events === '*') {
    return true;
  }

  for (let key in events) {
    const event = events[key];
    if (isIObfuscatedLoggedEvent(event)) {
      if (event.event.includes(eventType)) {
        return true;
      }
    } else if (typeof event === 'string' && event.includes(eventType)) {
      return true;
    }
  }
  return false;
}

function getObfuscatedEvent(
  eventType: string,
  events: (string | IObfuscatedLoggedEvent)[] | '*'
): IObfuscatedLoggedEvent | undefined {
  if (events === '*') {
    return undefined;
  }

  for (let key in events) {
    const event = events[key];
    if (isIObfuscatedLoggedEvent(event) && event.event.includes(eventType)) {
      return event;
    }
  }
  return undefined;
}

function obfuscateData(data: any, obfuscatedLoggedEvent?: IObfuscatedLoggedEvent): any {
  if (obfuscatedLoggedEvent === undefined) {
    return data;
  }

  const result = { ...data };
  for (let prop in data) {
    for (let key in obfuscatedLoggedEvent.obfuscated) {
      if (prop.includes(obfuscatedLoggedEvent.obfuscated[key])) {
        result[prop] = ObfuscatedDataValue;
      }
    }
  }
  return result;
}

export function createApplicationInsightsTracker(insights: ApplicationInsights) {
  const appState = useAppState.getState().appState || {};
  const urlSearch = new URLSearchParams(window.location.search);
  const clientId = urlSearch.get('client_id') || appState.clientId;
  const customerReference = urlSearch.get('customer_ref') || appState.customerReference;
  const configurationId = urlSearch.get('config_id') || appState.configurationId;
  const invitationId = urlSearch.get('invitation_id') || appState.invitationId;
  const language = navigator.language || appState.language || 'en';
  const userId = userIdStorage.getUserId();

  const commonData = {
    clientId,
    customerReference,
    configurationId,
    language,
    invitationId,
    userId
  };

  return {
    trackEvent(event: TrackableEvent) {
      if (!canTrack(insights) || !shouldTrack(event.type, loggedEvents)) return;

      const userId = userIdStorage.getUserId();

      const obfuscatedLoggedEvent = getObfuscatedEvent(event.type, loggedEvents);
      const telemetryEvent: IEventTelemetry = {
        name: event.type,
        properties: {
          data: {
            ...(event.payload ? obfuscateData(event.payload, obfuscatedLoggedEvent) : {}),
            ...commonData,
            userId
          },
          globals: {
            env: import.meta.env.MODE,
            correlationId: correlationId
          }
        }
      };

      insights.trackEvent(telemetryEvent);
      insights.flush();
    }
  };
}
